import {DocumentTypeMap} from './document-type-map';

// The active map — set by each project's strategy file at module load time.
let _map: DocumentTypeMap = {};

export function setDocumentTypeMap(map: DocumentTypeMap): void {
  _map = map;
}

/**
 * Checks whether a string value matches a candidate document type string.
 * Exported so project files can use it in their project-specific compound checkers
 * The value parameter accepts string so it is compatible with project-specific DocumentType
 * string enum values (DocumentType extends string, so passing DocumentType as string is safe).
 */
export function matchesDocumentType(value: string, candidate: string | undefined): boolean {
  return candidate !== undefined && typeof value === 'string' && value.trim().length > 0 && value === candidate;
}

// Checkers
export function documentTypeIsVisa(value: string): boolean {
  return matchesDocumentType(value, _map.visa);
}
export function documentTypeIsVisaArrival(value: string): boolean {
  return matchesDocumentType(value, _map.visaArrival);
}
export function documentTypeIsVisaDouble(value: string): boolean {
  return matchesDocumentType(value, _map.visaDouble);
}
export function documentTypeIsVisaArrivalDouble(value: string): boolean {
  return matchesDocumentType(value, _map.visaArrivalDouble);
}
export function documentTypeIsVisaMultiple(value: string): boolean {
  return matchesDocumentType(value, _map.visaMultiple);
}
export function documentTypeIsEntry(value: string): boolean {
  return matchesDocumentType(value, _map.entry);
}
export function documentTypeIsExit(value: string): boolean {
  return matchesDocumentType(value, _map.exit);
}
export function documentTypeIsBorderPass(value: string): boolean {
  return matchesDocumentType(value, _map.borderPass);
}
export function documentTypeIsOverstay(value: string): boolean {
  return matchesDocumentType(value, _map.overstay);
}
export function documentTypeIsProvisionalResidencePermit(value: string): boolean {
  return matchesDocumentType(value, _map.provisionalResidencePermit);
}
export function documentTypeIsFeedback(value: string): boolean {
  return matchesDocumentType(value, _map.feedback);
}
export function documentTypeIsPreEnroll(value: string): boolean {
  return matchesDocumentType(value, _map.preEnroll);
}
export function documentTypeIsUrgentPass(value: string): boolean {
  return matchesDocumentType(value, _map.urgentPass);
}
export function documentTypeIsEmbassyPass(value: string): boolean {
  return matchesDocumentType(value, _map.embassyPass);
}
export function documentTypeIsPassport(value: string): boolean {
  return matchesDocumentType(value, _map.passport);
}
export function documentTypeIsIdentityCard(value: string): boolean {
  return matchesDocumentType(value, _map.identityCard);
}
export function documentTypeIsResidenceCard(value: string): boolean {
  return matchesDocumentType(value, _map.residenceCard);
}
export function documentTypeIsScreen(value: string): boolean {
  return matchesDocumentType(value, _map.screen);
}
export function documentTypeIsPass(value: string): boolean {
  return matchesDocumentType(value, _map.pass);
}

// Compound Checkers

/** Project-specific visa types (e.g., KAZA). Register via setDocumentTypeMap in strategy. */
let _anyVisaExtra: ((value: string) => boolean) | undefined;
export function registerAnyVisaExtra(fn: (value: string) => boolean): void {
  _anyVisaExtra = fn;
}

/** Project-specific visa arrival variants. Register via strategy. */
let _visaArrivalVariantExtra: ((value: string) => boolean) | undefined;
export function registerVisaArrivalExtra(fn: (value: string) => boolean): void {
  _visaArrivalVariantExtra = fn;
}

/** Checks if value is any visa arrival variant (single, double, or project-specific). */
export function documentTypeIsVisaArrivalVariant(value: string): boolean {
  return documentTypeIsVisaArrival(value) ||
    documentTypeIsVisaArrivalDouble(value) ||
    (_visaArrivalVariantExtra ? _visaArrivalVariantExtra(value) : false);
}

/** Checks if value is any double-entry visa (arrival or local). */
export function documentTypeIsAnyDouble(value: string): boolean {
  return documentTypeIsVisaDouble(value) || documentTypeIsVisaArrivalDouble(value);
}

/** Entry mode of a visa operationId derived from the active map. */
export type VisaEntryMode = 'single' | 'double' | 'multiple';

/** Returns the entry mode based on the active map, or null if the operationId is not a visa entry type. */
export function getVisaEntryMode(value: string): VisaEntryMode | null {
  if(documentTypeIsVisaMultiple(value)) return 'multiple';
  if(documentTypeIsAnyDouble(value)) return 'double';
  if(documentTypeIsVisa(value) || documentTypeIsVisaArrival(value) || documentTypeIsEntry(value)) return 'single';
  return null;
}

/** Project-specific sticker label resolver (e.g. KAZA). Register via strategy. */
let _stickerLabelExtra: ((value: string) => string | null) | undefined;
export function registerStickerLabelExtra(fn: (value: string) => string | null): void {
  _stickerLabelExtra = fn;
}

const _defaultModeLabels: Record<VisaEntryMode, string> = {single: 'SINGLE ENTRY', double: 'DOUBLE ENTRY', multiple: 'MULTIPLE ENTRY'};

/** Returns the sticker label for an operationId, including project-specific overrides. Returns null for unknown types. */
export function getVisaEntryModeLabel(value: string): string | null {
  if(_stickerLabelExtra) {
    const extra = _stickerLabelExtra(value);
    if(extra !== null) return extra;
  }
  const mode = getVisaEntryMode(value);
  return mode ? _defaultModeLabels[mode] : null;
}

export function documentTypeIsAnyVisa(value: string): boolean {
  return documentTypeIsVisa(value) ||
    documentTypeIsVisaArrival(value) ||
    documentTypeIsVisaDouble(value) ||
    documentTypeIsVisaArrivalDouble(value) ||
    documentTypeIsVisaMultiple(value) ||
    documentTypeIsEntry(value) ||
    documentTypeIsExit(value) ||
    (_anyVisaExtra ? _anyVisaExtra(value) : false);
}

// Getters
export function getDocumentTypeIsVisa(): any {
  return _map.visa;
}
export function getDocumentTypeIsVisaArrival(): any {
  return _map.visaArrival;
}
export function getDocumentTypeIsVisaDouble(): any {
  return _map.visaDouble;
}
export function getDocumentTypeIsVisaArrivalDouble(): any {
  return _map.visaArrivalDouble;
}
export function getDocumentTypeIsVisaMultiple(): any {
  return _map.visaMultiple;
}
export function getDocumentTypeIsEntry(): any {
  return _map.entry;
}
export function getDocumentTypeIsExit(): any {
  return _map.exit;
}
export function getDocumentTypeIsBorderPass(): any {
  return _map.borderPass;
}
export function getDocumentTypeIsOverstay(): any {
  return _map.overstay;
}
export function getDocumentTypeIsProvisionalResidencePermit(): any {
  return _map.provisionalResidencePermit;
}
export function getDocumentTypeIsFeedback(): any {
  return _map.feedback;
}
export function getDocumentTypeIsPreEnroll(): any {
  return _map.preEnroll;
}
export function getDocumentTypeIsUrgentPass(): any {
  return _map.urgentPass;
}
export function getDocumentTypeIsEmbassyPass(): any {
  return _map.embassyPass;
}
export function getDocumentTypeIsPassport(): any {
  return _map.passport;
}
export function getDocumentTypeIsIdentityCard(): any {
  return _map.identityCard;
}
export function getDocumentTypeIsResidenceCard(): any {
  return _map.residenceCard;
}
export function getDocumentTypeIsScreen(): any {
  return _map.screen;
}
